/**
 * Law Firm Account Management Service
 *
 * Central export point for all account management functionality.
 * This service provides comprehensive tools for managing law firm profiles,
 * users, invitations, and related operations.
 */

// Export all types
export * from './types';

// Export all actions (from actions/ directory)
export * from './actions/';

// Export all utilities
export * from './utils';

// Export all hooks
export * from './hooks';

// ============================================================================
// Service Documentation
// ============================================================================

/**
 * @module Law Firm Account Management Service
 *
 * This service provides a complete solution for managing law firm accounts in the Vale platform.
 *
 * ## Features
 *
 * - **Law Firm Profile Management**: Update firm details, addresses, bank accounts
 * - **User Management**: Add, update, deactivate users within the firm
 * - **Invitation System**: Invite new users via email with tracking
 * - **Permissions**: Role-based access control for firm operations
 * - **Statistics**: Track firm usage and activity metrics
 *
 * ## Architecture
 *
 * The service is organized into four main modules:
 *
 * 1. **Types** (`./types.ts`): TypeScript interfaces and type definitions
 * 2. **Actions** (`./actions.ts`): Server actions for API operations
 * 3. **Utils** (`./utils.ts`): Helper functions for data manipulation
 * 4. **Hooks** (`./hooks.tsx`): React hooks for client-side state management
 *
 * ## Usage Examples
 *
 * ### Using Hooks in Components
 *
 * ```tsx
 * import { useLawFirmProfile, useLawFirmUsers } from '@/lib/services/account';
 *
 * function FirmDashboard() {
 *   const { profile, updateProfile, isLoading } = useLawFirmProfile();
 *   const { users, inviteUsers } = useLawFirmUsers();
 *
 *   // Use the data...
 * }
 * ```
 *
 * ### Using Server Actions
 *
 * ```tsx
 * import { getLawFirmProfileAction } from '@/lib/services/account';
 *
 * async function loadProfile() {
 *   const result = await getLawFirmProfileAction();
 *   if (result.success) {
 *     console.log(result.data);
 *   }
 * }
 * ```
 *
 * ### Using Utilities
 *
 * ```tsx
 * import { formatUserFullName, getUserStatusColor } from '@/lib/services/account';
 *
 * function UserCard({ user }) {
 *   return (
 *     <div className={getUserStatusColor(user.status)}>
 *       {formatUserFullName(user)}
 *     </div>
 *   );
 * }
 * ```
 *
 * ## Error Handling
 *
 * All actions return a consistent result format:
 *
 * ```typescript
 * {
 *   success: boolean;
 *   data?: T;
 *   error?: string;
 *   validationErrors?: Record<string, string>;
 *   message?: string;
 * }
 * ```
 *
 * ## Caching Strategy
 *
 * - Law Firm Profile: 5 minutes
 * - User List: 2 minutes
 * - Statistics: 10 minutes
 * - Permissions: 5 minutes
 *
 * ## Security Considerations
 *
 * - All operations require authentication
 * - User permissions are checked server-side
 * - Sensitive data (bank accounts) require admin privileges
 * - Invitation tokens expire after 14 days
 *
 * ## Future Enhancements
 *
 * - Real-time updates via WebSocket
 * - Bulk user operations
 * - Audit logging for all changes
 * - Custom role definitions
 * - Two-factor authentication for admins
 */
